import type {
	IContext,
	PineJS,
	ResolutionString,
	StudyMetaInfo,
} from "@/charting_library/charting_library";
import type { PriceInfo } from "../../../services/PriceService/PriceService";
import { Datasets, Subcharts, type Timeframes } from "../../../types";
import { EvToEbitdaIndicatorService } from "./custom-indicator-services/EvToEbitdaIndicatorService";
import { PowerdaysIndicatorService } from "./custom-indicator-services/PowerdaysIndicatorService";
import { PowerdayTrendIndicatorService } from "./custom-indicator-services/PowerdayTrendIndicatorService";
import { PriceMomentumSlopeIndicatorService } from "./custom-indicator-services/PriceMomentumSlopeIndicatorService";
import { TimingMomentumIndicatorService } from "./custom-indicator-services/TimingMomentumIndicatorService";
import { VolatilityStrengthIndicatorService } from "./custom-indicator-services/VolatilityStrengthIndicatorService";
import { DefaultIndicatorService } from "./DefaultIndicatorService";
import { DarkShortSlopeIndicatorService } from "./datasets-indicator-services/DarkShortSlopeIndicatorService";
import { DarkShortsIndicatorService } from "./datasets-indicator-services/DarkShortsIndicatorService";
import { PeFTMRatioIndicatorService } from "./datasets-indicator-services/PeFTMRatioIndicatorService";
import { PeTTMRatioIndicatorService } from "./datasets-indicator-services/PeTTMRatioIndicatorService";
import { ShortInterestIndicatorService } from "./datasets-indicator-services/ShortInterestIndicatorService";
import { CashFlowIndicatorService } from "./Quarterly-indicator-services/CashFlowIndicatorService";
import { EPSIndicatorService } from "./Quarterly-indicator-services/EPSIndicatorService";
import { ProfitMarginIndicatorService } from "./Quarterly-indicator-services/ProfitMarginIndicatorService";
import { SalesIndicatorService } from "./Quarterly-indicator-services/SalesIndicatorService";
import { RSEpsFinnhubIndicatorService } from "./Revisions-indicator-services/RSEpsFinnhubIndicatorService";
import { RSEpsZacksIndicatorService } from "./Revisions-indicator-services/RSEpsZacksIndicatorService";
import { RSSalesFinnhubIndicatorService } from "./Revisions-indicator-services/RSSalesFinnhubIndicatorService";
import { RSSalesZacksIndicatorService } from "./Revisions-indicator-services/RSSalesZacksIndicatorService";
import { IndexRatioTrendSlopeIndicatorService } from "./RMR-indicator-services/IndexRatioTrendSlopeIndicatorService";
import { RMRCompositeIndicatorService } from "./RMR-indicator-services/RMRCompositeIndicatorService";
import { RMRMonthsIndicatorService } from "./RMR-indicator-services/RMRMonthsIndicatorService";
import { RMRSlopeIndicatorService } from "./RMR-indicator-services/RMRSlopeIndicatorService";
import { SectorAndIndexRatioTrendIndicatorService } from "./RMR-indicator-services/SectorAndIndexRatioTrendIndicatorService";

export type Indicator = Subcharts | Datasets | "";

export type IndicatorsService = {
	getBarData: (
		from: number,
		to: number,
		interval: Timeframes,
		ticker: string,
	) => Promise<PriceInfo[]>;

	plots?: StudyMetaInfo["plots"];
	styles?: StudyMetaInfo["styles"];
	defaults?: StudyMetaInfo["defaults"];
	palettes?: StudyMetaInfo["palettes"];
	interval?: ResolutionString;

	getPlotsData?: (PineJS: PineJS, context: IContext) => number[];
};

export const getIndicatorsService = (
	indicator: Indicator,
): IndicatorsService => {
	switch (indicator) {
		case Subcharts.TimingMomentum:
			return new TimingMomentumIndicatorService();
		case Subcharts.Powerdays:
			return new PowerdaysIndicatorService();
		case Subcharts.PwdTrend:
			return new PowerdayTrendIndicatorService();
		case Subcharts.RMRComp:
			return new RMRCompositeIndicatorService();
		case Subcharts.RMRMonthly:
			return new RMRMonthsIndicatorService();
		case Subcharts.RMRSlope:
			return new RMRSlopeIndicatorService();
		case Subcharts.PriceMomSlope:
			return new PriceMomentumSlopeIndicatorService();
		case Subcharts.InxRatTrendSlope:
			return new IndexRatioTrendSlopeIndicatorService();
		case Subcharts.VolStrength:
			return new VolatilityStrengthIndicatorService();
		case Subcharts.SectInxRatTrend:
			return new SectorAndIndexRatioTrendIndicatorService();
		case Datasets.PETTM:
			return new PeTTMRatioIndicatorService();
		case Datasets.PEFTM:
			return new PeFTMRatioIndicatorService();
		case Datasets.EPS:
			return new EPSIndicatorService();
		case Datasets.Sales:
			return new SalesIndicatorService();
		case Datasets.ProfMargin:
			return new ProfitMarginIndicatorService();
		case Datasets.FreeCFlow:
			return new CashFlowIndicatorService();
		case Datasets.RSEF:
			return new RSEpsFinnhubIndicatorService();
		case Datasets.RSEZ:
			return new RSEpsZacksIndicatorService();
		case Datasets.RSSF:
			return new RSSalesFinnhubIndicatorService();
		case Datasets.RSSZ:
			return new RSSalesZacksIndicatorService();
		case Datasets.EVEb:
			return new EvToEbitdaIndicatorService();
		case Datasets.ShortInt:
			return new ShortInterestIndicatorService();
		case Datasets.DarkShort:
			return new DarkShortsIndicatorService();
		case Datasets.DarkShortSlope:
			return new DarkShortSlopeIndicatorService();

		default:
			return new DefaultIndicatorService();
	}
};
